import { word } from "@modules/core/utils/i18n"
import { ProgramProgressionStatus } from "@modules/programs/programOnOffTypes"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { getProgramProgressionStatusIcon } from "@modules/programs/programSubscriber/utils/programSubscriberUtils"
import { Circles } from "@modules/ui/components/circles"
import { HUText } from "@modules/ui/components/huText"
import dayjs from "dayjs"
import { useMemo } from "react"
import { useTheme } from "styled-components"

type ProgramSubscriberDetailsContentOnOffStatusProps = {
  subscriberDetails: ProgramSubscriberEntity
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscriberDetailsContentOnOffStatus({
  subscriberDetails,
  className,
  ...props
}: ProgramSubscriberDetailsContentOnOffStatusProps) {
  const defautlClassName = "flex flex-column gap-2 w-full"
  const theme = useTheme()

  const subscriptionInfos = subscriberDetails.getProgramSubscriptionOnOffStatusInfos(theme)
  const isPaused = subscriberDetails.onOffProgramProgression?.status === ProgramProgressionStatus.STOPPED
  const isCompleted = subscriberDetails.onOffProgramProgression?.status === ProgramProgressionStatus.COMPLETED
  const mainTextColor = isCompleted ? theme.neutralColor900 : isPaused ? theme.neutralColor600 : theme.neutralColor900

  const fillRatePoints = useMemo(() => {
    if (
      !subscriberDetails.onOffProgramProgression ||
      subscriberDetails.onOffProgramProgression.status === ProgramProgressionStatus.NOT_STARTED
    )
      return 0

    const fillRate =
      !subscriberDetails.onOffProgramProgression.currentIndex || !subscriberDetails.onOffProgramProgression.duration
        ? 0
        : ((subscriberDetails.onOffProgramProgression.currentIndex + 1) /
            subscriberDetails.onOffProgramProgression.duration) *
          100
    const fillRatePointsValue = Math.round(fillRate / 10)
    return Math.min(10, Math.max(0, fillRatePointsValue))
  }, [subscriberDetails.onOffProgramProgression])

  return (
    <div className={className ?? defautlClassName} {...props}>
      <div className="grid">
        <div className="col-6 pr-3 flex flex-column ">
          {/*STATUS */}
          <div className="flex flex-column gap-2">
            <div className="flex w-full gap-2 align-items-baseline">
              <HUText fontStyle="BS" color={theme.neutralColor700}>
                {word("program.subscribers.subscriptionDetails.status.title")}
              </HUText>
              <div className="flex flex-grow-1 border-top-1 border-neutralColor-300" />
            </div>
            <div className="flex flex-row gap-2 align-items-center">
              {subscriberDetails.onOffProgramProgression
                ? getProgramProgressionStatusIcon(
                    subscriberDetails.onOffProgramProgression.status,
                    subscriptionInfos.color,
                  )
                : null}
              <div className="flex flex-column gap-0">
                <HUText fontStyle="LL" color={theme.neutralColor900}>
                  {subscriptionInfos.label}
                </HUText>
                {subscriptionInfos.information ? (
                  <HUText fontStyle="BS" color={theme.neutralColor700}>
                    {subscriptionInfos.information}
                  </HUText>
                ) : null}
              </div>
            </div>
          </div>
          {/* DATE OF PURCHASE */}
          <div className="flex flex-column gap-2 my-auto">
            <div className="flex w-full gap-2 align-items-baseline">
              <HUText fontStyle="BS" color={theme.neutralColor700}>
                {word("program.subscribers.subscriptionDetails.dateOfPurchase.label")}
              </HUText>
              <div className="flex flex-grow-1 border-top-1 border-neutralColor-300" />
            </div>
            <HUText fontStyle="LL" color={theme.neutralColor900}>
              {dayjs(subscriberDetails.subscriptionDate).format("L")}
            </HUText>
          </div>
        </div>
        <div className="col-6 pl-3 flex flex-column gap-2">
          {/* PROGRESS */}
          <div className="flex flex-row gap-2 align-items-baseline">
            <HUText fontStyle="BS" color={theme.neutralColor700}>
              {word("program.subscribers.subscriptionDetails.progress.label")}
            </HUText>
            <div className="flex flex-grow-1 border-top-1 border-neutralColor-300" />
          </div>
          {/* CIRCLES */}
          <div className="flex flex-column gap-2 align-items-center">
            {subscriberDetails.onOffProgramProgression ? (
              <>
                <HUText fontStyle="BM" className="mb-1" color={theme.neutralColor900}>
                  {`${fillRatePoints} / ${subscriberDetails.onOffProgramProgression.duration} ${word("global.day.label", { count: subscriberDetails.onOffProgramProgression.duration })}`}
                </HUText>

                <Circles count={10} filled={fillRatePoints} progressionstyle="circle" color={mainTextColor} />
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
